import { TimezoneDropdownInput } from "#src/batteries-included-components";
import {
  useAuthenticatedContext,
  useIsFeatureAvailable,
} from "#src/contexts/AuthenticatedContext.helpers";
import { useQuery } from "@tanstack/react-query";
import {
  CheckboxInput,
  Icon,
  Panel,
  TelephoneInput,
  TextInput,
  Tooltip,
  useFormContext,
} from "@validereinc/common-components";
import {
  Resources,
  UserStatus,
  UserTypeEnum,
  UsersAdapter,
} from "@validereinc/domain";
import { FeatureFlags, Insights } from "@validereinc/domain-controllers";
import classNames from "classnames/bind";
import React, { useContext } from "react";
import { z } from "zod";
import { UserDetailContext } from "#src/routes/settings/users/detail/UserDetailContext";
import styles from "./EditUserFormPanel.module.scss";

const { getNumberOfUsersThatCanAccessQuicksightConsole } = Insights.logic;

const cx = classNames.bind(styles);

export const UserUpdateFormSchema = z
  .object({
    type: z.nativeEnum(UserTypeEnum),
    name: z.string(),
    email: z.string().email(),
    phone: z.string().nullable(),
    timezone: z.string(),
    can_access_quicksight_dashboards: z.boolean(),
    can_access_quicksight_console: z.boolean(),
    status: z.nativeEnum(UserStatus),
    roles: z.array(z.string()),
  })
  .partial();
export type UserUpdateFormType = z.infer<typeof UserUpdateFormSchema>;

export const EditUserFormPanel = () => {
  const {
    v2: {
      companyInfo: { company },
    },
  } = useAuthenticatedContext();
  const { userDetails } = useContext(UserDetailContext) ?? {};
  const userData = userDetails?.data;
  const isHuman = userData?.type === UserTypeEnum.human;

  const [isManageInsightsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: {
      $and: [FeatureFlags.DashboardsManageInsights],
    },
  });

  const form = useFormContext<UserUpdateFormType>();
  const formValues = form.watch();

  const { data: usersData, isLoading: isUsersLoading } = useQuery({
    queryKey: [Resources.USER],
    queryFn: async () => {
      return UsersAdapter.getList({});
    },
  });
  const numberOfUsersThatCanAccessQuicksightConsole =
    getNumberOfUsersThatCanAccessQuicksightConsole(usersData?.data ?? []);

  const isCompanyQuicksightConsoleSeatLimitReached =
    numberOfUsersThatCanAccessQuicksightConsole >=
    (company?.quicksight_console_seat_limit ?? 0);
  const canEnableCanAccessQuicksightConsole =
    !formValues.can_access_quicksight_console &&
    isCompanyQuicksightConsoleSeatLimitReached;

  const isLoaded = !isUsersLoading;

  return (
    <>
      <Panel title="Details">
        <div style={{ width: 250 }}>
          <TextInput
            name="name"
            label="Name"
            placeholder="Name"
            isRequired
          />
          {isHuman && (
            <>
              <TextInput
                name="email"
                label="Email"
                placeholder="Email"
                isDisabled
                isRequired
              />
              <TelephoneInput
                name="phone"
                label="Phone Number"
              />
            </>
          )}
          <TimezoneDropdownInput name="timezone" />
        </div>
      </Panel>
      {isHuman && (
        <Panel
          title="Roles & Permissions"
          loaded={isLoaded}
        >
          <CheckboxInput
            name="can_access_quicksight_dashboards"
            label="User has access to Insights Dashboards"
            isLabelShown={false}
            isFluid
          />
          {isManageInsightsAvailable && (
            <div className={cx("checkbox-with-tooltip-container")}>
              <CheckboxInput
                componentClassName={cx(
                  "can-access-quicksight-console-checkbox"
                )}
                className={cx("can-access-quicksight-console-checkbox")}
                name="can_access_quicksight_console"
                label="User has access to Manage Insights"
                isLabelShown={false}
                isDisabled={canEnableCanAccessQuicksightConsole}
                isFluid
              />
              {canEnableCanAccessQuicksightConsole && (
                <Tooltip
                  content={`This company has reached its limit of ${company?.quicksight_console_seat_limit} users that can access Manage Insights`}
                  contentClassName={cx("tooltip-container")}
                >
                  <Icon
                    className={cx("tooltip-icon")}
                    variant="info"
                    size={16}
                  />
                </Tooltip>
              )}
            </div>
          )}
        </Panel>
      )}
    </>
  );
};
